import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import { log } from "../../logger";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { BarcodeIcon } from "../../shared/components/icons";
import { RemoveIcon, AddIcon } from "../../shared/components/icons";
import { requests } from "../../shared/api";
import type { ContainerItemResponse } from "../../shared/api/requests";
import { BulkMoveBar, BULK_MOVE_BAR_SPACER, BULK_MOVE_SNACKBAR_OFFSET } from "../../shared/components/BulkMoveBar";
import { ItemBulkMoveDialog, type ItemBulkMoveOutcome } from "./ItemBulkMoveDialog";
import { useAppStore } from "../../shared/store/app";
import { usePreferencesStore } from "../../shared/store/preferences";
import { mergeMirrorRows } from "../search/catalogStore";
import { ItemThumbnail } from "./ItemThumbnail";
import { useT } from "../../shared/i18n";
import {
  compareByName,
  compareBySoonestDeadline,
  compareItemCategories,
  matchesDeadlineFilters,
  selectAllBulkSelection,
  toggleBulkSelection,
  type DeadlineFilter,
  type Item,
} from "@egalregal/shared";
import {
  DeadlineColumn,
  DeadlineColumnHeader,
  DeadlineStatusDot,
  ITEM_DEADLINE_VOCABULARY,
} from "../../shared/components/Deadlines";
import { ItemMenu } from "./ItemMenu";
import { itemCategoryLabel } from "../../shared/components/CategoryChip";
import { placementFailureNotice } from "./placementNotice";
import { usePlacementsStore } from "./placementsStore";
import { useDemoMode } from "../demo/useDemoMode";

interface ItemListProps {
  containerId: string;
  onScanRequest?: () => void;
  onItemAdded?: () => void;
  refreshKey?: number;
}

export function ItemList({ containerId, onScanRequest, refreshKey }: ItemListProps) {
  const t = useT();
  const demoMode = useDemoMode();
  const navigate = useNavigate();
  const [items, setItems] = useState<ContainerItemResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  // Same persisted opt-in deadline view as the global list (deadlines 02).
  const deadlineView = usePreferencesStore((s) => s.itemDeadlineView);
  const setDeadlineView = usePreferencesStore((s) => s.setItemDeadlineView);
  // Name order is the header's default while the deadline view is on; with the
  // view off the rows keep the plain category order (the guards below).
  const [deadlineSort, setDeadlineSort] = useState<"category" | "name" | "soonest">("name");
  // Threshold-driven, client-side filter (deadlines 04) using the shared
  // predicate and the same item vocabulary as the server.
  const [deadlineFilters, setDeadlineFilters] = useState<DeadlineFilter[]>([]);
  // Item bulk mode is local selection state (bulk-move 06), like the global
  // item list's bulk mode: leaving the container or a full success drops it.
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [notice, setNotice] = useState<ItemBulkNotice | null>(null);
  // The source container's entry and home owner gate the same-home destination
  // list. The browse selection answers when it is entry-scoped; otherwise the
  // container-entry response resolves both (bulk-move 06).
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const selectEntry = useAppStore((s) => s.selectEntry);
  const [sourceEntry, setSourceEntry] = useState<SourceEntry | null>(null);
  // Quantity writes go through the placement ledger (placement-ledger 03); a
  // failed write keeps the row's stored total and reports inline.
  const setTotal = usePlacementsStore((state) => state.setTotal);
  const removePlacement = usePlacementsStore((state) => state.remove);
  const [quantityError, setQuantityError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const resolveSourceEntry = async () => {
      if (selectedEntryId !== undefined) {
        const share = sharedWithMe.find((entry) => entry.entry.id === selectedEntryId);
        const own = entries.some((entry) => entry.id === selectedEntryId);
        if (share || own) {
          setSourceEntry({ id: selectedEntryId, owner: share?.owner });
          return;
        }
      }
      try {
        const resolved = await requests.containers.entry(containerId);
        if (cancelled) return;
        const own = entries.some((entry) => entry.id === resolved.entry.id);
        setSourceEntry({
          id: resolved.entry.id,
          owner: own ? undefined : resolved.entry.owner,
        });
      } catch (err) {
        log.warn(
          { err, containerId },
          "Failed to resolve the source entry for the item bulk move",
        );
        if (!cancelled) setSourceEntry(null);
      }
    };
    void resolveSourceEntry();
    return () => {
      cancelled = true;
    };
  }, [containerId, selectedEntryId, entries, sharedWithMe]);

  // A different container starts without a selection or an open dialog, and a
  // refusal belongs to the row it was raised for.
  useEffect(() => {
    setBulkMode(false);
    setSelected(new Set());
    setMoveOpen(false);
    setQuantityError(null);
  }, [containerId]);

  const loadItems = useCallback(async () => {
    try {
      const data = await requests.items.list(containerId);
      // The server snapshot is authoritative (placement-ledger 01): the row
      // seam is a pass-through, never a mirror fallback.
      setItems(mergeMirrorRows(containerId, data));
      // Keep the local search mirror in step with this client's mutations
      // (ticket 07): a failed sync leaves the stored mirror untouched.
      void useAppStore.getState().refreshItemMirror();
    } catch (err) {
      log.warn({ err }, "Failed to load items");
      // Nothing is queued offline, so a failed read shows no rows rather than
      // a phantom placement the server never confirmed.
      setItems(mergeMirrorRows(containerId, []));
    } finally {
      setLoading(false);
    }
  }, [containerId]);

  useEffect(() => {
    void loadItems();
  }, [loadItems, refreshKey]);

  /**
   * Write one row's absolute total through the ledger (placement-ledger 03).
   * The mirror is written by the ledger, so the list no longer re-syncs after
   * every tap; a saved outcome updates the row in place, a zero removes it, and
   * a failed write keeps the stored total and reports inline instead of
   * rejecting unhandled.
   */
  const applyQuantity = async (row: ContainerItemResponse, next: number) => {
    if (!row.item) return;
    // The row's item type predates the shared schema (it omits `description`),
    // but the wire payload is a full Item. The ledger only reads identity and
    // barcode from it; the mirror is written from the server's saved item.
    const item = row.item as Item;
    const outcome =
      next <= 0
        ? await removePlacement(containerId, item)
        : await setTotal(containerId, item, next);
    if (outcome.status !== "saved") {
      log.warn(
        { outcome, itemId: row.itemId, containerId },
        "Failed to change the placement quantity",
      );
      setQuantityError(placementFailureNotice(t, outcome));
      return;
    }
    setQuantityError(null);
    if (next <= 0) {
      setItems((prev) => prev.filter((ci) => ci.itemId !== row.itemId));
      return;
    }
    setItems((prev) =>
      prev.map((ci) => (ci.itemId === row.itemId ? { ...ci, quantity: outcome.quantity } : ci)),
    );
  };

  const handleQuantityChange = (row: ContainerItemResponse, newQty: number) => {
    void applyQuantity(row, newQty);
  };

  const startEditQty = (itemId: string, currentQty: number) => {
    setEditingId(itemId);
    setEditingValue(String(currentQty));
  };

  const commitEditQty = async (itemId: string) => {
    const parsed = parseInt(editingValue, 10);
    setEditingId(null);
    if (isNaN(parsed) || parsed < 0) return;
    const row = items.find((ci) => ci.itemId === itemId);
    if (!row) return;
    await applyQuantity(row, parsed);
  };

  if (loading) {
    return null;
  }

  const rows = items.filter((ci) => ci.item);
  const filteredRows =
    deadlineFilters.length === 0
      ? rows
      : rows.filter((ci) =>
          matchesDeadlineFilters(ci.item.properties, deadlineFilters, ITEM_DEADLINE_VOCABULARY),
        );
  const sorted = [...filteredRows].sort((a, b) => {
    if (deadlineView && deadlineSort === "soonest") {
      return compareBySoonestDeadline(a.item, b.item);
    }
    if (deadlineView && deadlineSort === "name") {
      return compareByName(a.item, b.item);
    }
    return compareItemCategories(a.item.category, b.item.category) || compareByName(a.item, b.item);
  });

  const handleDeadlineViewChange = (enabled: boolean) => {
    setDeadlineView(enabled);
    // Turning the view on reveals the header in name order; turning it off
    // returns the rows to their plain category layout.
    setDeadlineSort(enabled ? "name" : "category");
    if (!enabled) setDeadlineFilters([]);
  };
  const toggleDeadlineSort = () =>
    setDeadlineSort((current) => (current === "soonest" ? "name" : "soonest"));

  const toggleBulkMode = () => {
    setBulkMode((active) => !active);
    setSelected(new Set());
  };

  /** Row toggle; the product cap refuses the 51st row without clearing the rest. */
  const toggleSelection = (itemId: string) =>
    setSelected((current) => toggleBulkSelection(current, itemId));

  /** Select-all takes the visible rows in order and stops at the 50-row cap. */
  const selectAll = () =>
    setSelected((current) =>
      selectAllBulkSelection(sorted.map((row) => row.itemId), current),
    );

  const selectedItems = items.flatMap((row) =>
    row.item && selected.has(row.itemId) ? [{ id: row.itemId, name: row.item.name }] : [],
  );

  const completeMove = (outcome: ItemBulkMoveOutcome) => {
    setMoveOpen(false);
    setBulkMode(false);
    setSelected(new Set());
    setNotice({
      severity: "success",
      message: t("itemBulkMoveSuccess", {
        count: String(outcome.result.moved.length),
        destination: outcome.destinationLabel,
      }),
      open: { entryId: outcome.entryId, containerId: outcome.containerId },
    });
    // Stay in the source view and reload its placements; loadItems also
    // refreshes the search mirror after the commit.
    void loadItems();
  };

  /** The snackbar link scopes the destination entry before opening the container. */
  const openDestination = async () => {
    const target = notice?.open;
    if (!target) return;
    setNotice(null);
    // Navigate first: selectEntry clears the container slice, and the source
    // /browse/:id route would otherwise resolve its old entry during the
    // switch (the same race as the container bulk-move link).
    navigate(`/browse/${target.containerId}`);
    if (target.entryId !== selectedEntryId) await selectEntry(target.entryId);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1 }}
        data-testid="items-header"
      >
        <Typography variant="overline" color="text.secondary" sx={{ flexGrow: 1 }}>
          {t("items")} · {sorted.length}
        </Typography>
        <ItemMenu
          deadlineView={deadlineView}
          onToggleDeadlineView={handleDeadlineViewChange}
          deadlineFilters={deadlineFilters}
          onDeadlineFilters={setDeadlineFilters}
          bulkMode={bulkMode}
          // Entering bulk mode needs the source entry; leaving it never does.
          canSelect={bulkMode || sourceEntry !== null}
          onToggleBulkMode={toggleBulkMode}
        />
        {/* The primary action of the items section stays rightmost. */}
        {!demoMode && (
          <IconButton
            size="small"
            aria-label={t("scanBarcode")}
            onClick={onScanRequest}
            color="primary"
          >
            <BarcodeIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      {quantityError && (
        <Typography
          variant="body2"
          color="error"
          data-testid="item-quantity-error"
          sx={{ mb: 1 }}
        >
          {quantityError}
        </Typography>
      )}

      {bulkMode && sorted.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Checkbox
            checked={sorted.every((row) => selected.has(row.itemId))}
            indeterminate={sorted.some((row) => selected.has(row.itemId)) && !sorted.every((row) => selected.has(row.itemId))}
            onChange={selectAll}
            inputProps={{ "aria-label": t("bulkSelectAll") }}
          />
          <Typography variant="body2">{t("bulkSelectAll")}</Typography>
        </Stack>
      )}

      {deadlineView && sorted.length > 0 && (
        <DeadlineColumnHeader
          label={t("items")}
          soonest={deadlineSort === "soonest"}
          onToggleSort={toggleDeadlineSort}
        />
      )}

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {rows.length > 0 && deadlineFilters.length > 0
            ? t("deadlineFilterEmpty")
            : t("itemsEmpty")}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {sorted.map((ci) => ci.item && (
            <Card
              key={ci.itemId}
              variant="outlined"
              onClick={bulkMode ? () => toggleSelection(ci.itemId) : undefined}
              sx={bulkMode ? { cursor: "pointer" } : undefined}
              data-testid={`item-row-${ci.itemId}`}
            >
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {bulkMode && (
                    <Checkbox
                      checked={selected.has(ci.itemId)}
                      onChange={() => toggleSelection(ci.itemId)}
                      onClick={(event) => event.stopPropagation()}
                      inputProps={{ "aria-label": ci.item.name }}
                    />
                  )}
                  <ItemThumbnail
                    item={ci.item}
                    onClick={bulkMode ? undefined : () => navigate(`/items/${ci.itemId}`)}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2" noWrap>
                        {ci.item.name}
                      </Typography>
                      {!deadlineView && <DeadlineStatusDot properties={ci.item.properties} />}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip label={itemCategoryLabel(ci.item.category, t)} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                      <Typography variant="caption" color="text.secondary">
                        {ci.item.barcode}
                      </Typography>
                    </Stack>
                  </Box>
                  {deadlineView && (
                    <Box sx={{ width: { xs: 132, sm: 176 }, flexShrink: 0 }}>
                      <DeadlineColumn properties={ci.item.properties} />
                    </Box>
                  )}
                  {/* Quantity edits are disabled while the row toggles selection
                      and absent in the read-only demo. */}
                  {!bulkMode && !demoMode && (
                    <Stack direction="row" alignItems="center" spacing={0}>
                      <IconButton
                        size="small"
                        aria-label={t("decrementQuantity")}
                        onClick={() => handleQuantityChange(ci, ci.quantity - 1)}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      {editingId === ci.itemId ? (
                        <TextField
                          size="small"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => void commitEditQty(ci.itemId)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") void commitEditQty(ci.itemId);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                          slotProps={{ htmlInput: { style: { width: 36, textAlign: "center", padding: "2px 4px", fontSize: "0.875rem" } } }}
                          autoFocus
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          data-testid={`item-quantity-${ci.itemId}`}
                          sx={{ minWidth: 24, textAlign: "center", cursor: "pointer", "&:hover": { bgcolor: "grey.100", borderRadius: 1 } }}
                          onClick={() => startEditQty(ci.itemId, ci.quantity)}
                        >
                          {ci.quantity}
                        </Typography>
                      )}
                      <IconButton
                        size="small"
                        aria-label={t("incrementQuantity")}
                        onClick={() => handleQuantityChange(ci, ci.quantity + 1)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Let the last rows scroll clear of the fixed bar. */}
      {bulkMode && !demoMode && <Box sx={{ height: BULK_MOVE_BAR_SPACER }} aria-hidden />}

      {bulkMode && !demoMode && !moveOpen && (
        <BulkMoveBar count={selected.size} onMove={() => setMoveOpen(true)} />
      )}

      {sourceEntry && !demoMode && (
        <ItemBulkMoveDialog
          open={moveOpen}
          sourceContainerId={containerId}
          sourceEntryId={sourceEntry.id}
          sourceOwner={sourceEntry.owner}
          items={selectedItems}
          onClose={() => setMoveOpen(false)}
          onComplete={completeMove}
        />
      )}

      <Snackbar
        open={notice !== null}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: bulkMode ? BULK_MOVE_SNACKBAR_OFFSET : undefined }}
      >
        <Alert
          severity={notice?.severity ?? "success"}
          onClose={() => setNotice(null)}
          action={
            notice?.open ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => void openDestination()}
                data-testid="item-bulk-move-open"
              >
                {t("open")}
              </Button>
            ) : undefined
          }
        >
          <span data-testid="item-bulk-move-notice">{notice?.message}</span>
        </Alert>
      </Snackbar>
    </Box>
  );
}

/** A committed item batch surfaced in the list's snackbar. */
interface ItemBulkNotice {
  severity: "success";
  message: string;
  open?: { entryId: string; containerId: string };
}

/** The source container's entry and home owner, resolved for the destination list. */
interface SourceEntry {
  id: string;
  owner?: string;
}
